import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Réservations du vendeur.
// Affiche les réservations prises sur les services de réservation
// (ZCalendar) rattachés au vendeur.
// ─────────────────────────────────────────────────────────────

interface BookingService {
  id: number;
  service_name: string | null;
}

interface Appointment {
  id: number;
  service_id: number | null;
  fullname: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  date: string | null;
  time: string | null;
  status: string | null;
  created_at: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-accent-100 text-accent-700',
  pending: 'bg-secondary-100 text-secondary-900',
  cancelled: 'bg-red-50 text-red-600',
};

function statusStyle(status: string | null): string {
  const key = (status || 'pending').toLowerCase();
  return STATUS_STYLES[key] || 'bg-background-100 text-foreground-600';
}

export default function VendorReservations({ vendorId }: { vendorId: number }) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [serviceNames, setServiceNames] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!vendorId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Services de réservation du vendeur.
      const { data: services } = await supabase
        .from('booking_services')
        .select('id, service_name')
        .eq('owner', vendorId);
      const svcRows = (services as BookingService[] | null) || [];
      const names = new Map<number, string>();
      svcRows.forEach((s) => names.set(Number(s.id), s.service_name || 'Service'));
      setServiceNames(names);

      const ids = svcRows.map((s) => Number(s.id)).filter((n) => Number.isFinite(n));
      if (ids.length === 0) {
        setAppointments([]);
        return;
      }

      const { data: appts, error: apptErr } = await supabase
        .from('booking_appointments')
        .select('id, service_id, fullname, email, phone, message, date, time, status, created_at')
        .in('service_id', ids)
        .order('date', { ascending: false });
      if (apptErr) throw apptErr;
      setAppointments((appts as Appointment[] | null) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
        <i className="ri-error-warning-line"></i>
        {error}
        <button onClick={load} className="ml-auto underline cursor-pointer">Réessayer</button>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-calendar-check-line text-2xl text-foreground-400"></i>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Aucune réservation</h3>
        <p className="text-sm text-foreground-500 max-w-xs">
          Les réservations prises sur vos services de réservation apparaîtront ici.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-foreground-500">
          {appointments.length} réservation{appointments.length > 1 ? 's' : ''}
        </p>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-background-100/60 text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Service</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Client</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Date</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">Statut</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a) => (
                <tr key={a.id} className="border-t border-background-200/70">
                  <td className="px-4 py-3 text-sm text-foreground-900">
                    {a.service_id && serviceNames.get(Number(a.service_id)) ? serviceNames.get(Number(a.service_id)) : 'Service'}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-600">
                    <div className="font-medium text-foreground-800">{a.fullname || '—'}</div>
                    <div className="text-[11px] text-foreground-400">
                      {[a.phone, a.email].filter(Boolean).join(' · ') || ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-500">
                    {a.date ? new Date(a.date).toLocaleDateString('fr-FR') : '—'}
                    {a.time ? ` · ${String(a.time).slice(0, 5)}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${statusStyle(a.status)}`}>
                      {a.status || 'En attente'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}